import React, { useState } from "react";
import styled from "styled-components/macro";
import { Scrollbars, positionValues } from "react-custom-scrollbars-2";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";

interface VerticalScrollProps {
    leftSided: boolean;
}

/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-unused-vars */
const overlayCss = (direction: string, toDirection: string, isHorizontal: boolean, fill: boolean) => `linear-gradient(
    ${toDirection},
    black ${fill ? `90%` : `80%`}, transparent
)${direction}${isHorizontal ? `/${fill ? `100%` : `51%`} 100%` : `/100% ${fill ? `100%` : `51%`}`}`;

// Fixes ios fade issue.
const dummyOverlay = (direction: string) => `linear-gradient(
    ${direction},
    rgba(0, 0, 0, 0) 0px,
    rgba(0, 0, 0, 1) 1px
)`;

const TrackHorizontal = styled.div`
    position: absolute;
    height: 18px;
    right: 2px;
    bottom: 6px;
    left: 2px;
    border-radius: 3px;
`;

const ThumbHorizontal = styled.div`
    position: relative;
    height: 100%;
    cursor: pointer;
    border-radius: inherit;
`;

const HandleHorizontal = styled.div`
    background-color: rgba(191, 191, 191, 1);
    border-radius: inherit;
    height: 6px;
    top: 6px;
    width: 100%;
`;

const TrackVertical = styled.div<VerticalScrollProps>`
    position: absolute;
    width: 18px;
    ${props => (props.leftSided ? `left: 6px` : `right: 2px`)};
    bottom: 2px;
    top: 2px;
    border-radius: 3px;
`;

const ThumbVertical = styled.div`
    position: relative;
    width: 100%;
    cursor: pointer;
    border-radius: inherit;
`;

const HandleVertical = styled.div`
    position: absolute;
    background-color: rgba(204, 204, 204, 1);
    border-radius: inherit;
    width: 6px;
    left: 6px;
    height: 100%;
`;

// The -100px is there to hide the native scrollbar, however it also seems to
// hide the mobile native scrollbar and the scrollbar library doesn't seem to
// render any scrollbars on mobile. The seemingly excessive 100px is needed to
// keep it hidden when the user zooms out by using the browser built-in scale
// featere.
const ScrollView = styled.div<ScrollViewProps>`
    position: ${props => (props.dynamicHeight ? "relative" : "absolute")};
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    overflow-y: ${props => (props.renderVertical ? "auto" : "hidden")};
    overflow-x: ${props => (props.renderHorizontal ? "auto" : "hidden")};
    margin-right: -100px;
    padding-right: 100px;
    margin-bottom: ${props => (props.dynamicHeight ? "0px" : "-100px")};
    padding-bottom: ${props => (props.dynamicHeight ? "0px" : "100px")};
    ${props => (props.leftSided ? `margin-left: 30px` : ``)};
    max-height: ${props => props.maxHeight};
    mask: ${props => {
        if (props.hideFade) return null;
        const background = [];

        const fillHorizontal = !props.showLeftOverlay || !props.showRightOverlay;

        if (props.showRightOverlay) {
            background.push(overlayCss("right", "to right", true, fillHorizontal));
        }

        if (props.showLeftOverlay) {
            background.push(overlayCss("left", "to left", true, fillHorizontal));
        }

        const fillVertical = !props.showBottomOverlay || !props.showTopOverlay;

        if (props.showBottomOverlay) {
            background.push(overlayCss("bottom", "to bottom", false, fillVertical));
        }

        if (props.showTopOverlay) {
            background.push(overlayCss("top", "to top", false, fillVertical));
        }

        return `${background.join(",")};`;
    }};
    mask-repeat: no-repeat;
`;

interface ScrollViewProps {
    isMobile: boolean;
    renderVertical: boolean;
    renderHorizontal: boolean;
    hideFade: Boolean;
    showTopOverlay: Boolean;
    showLeftOverlay: Boolean;
    showRightOverlay: Boolean;
    showBottomOverlay: Boolean;
    maxHeight: string;
    dynamicHeight: boolean;
    leftSided: boolean;
}

const Container = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    position: relative;
`;

const CustomScrollbar = ({
    children,
    maxHeight,
    onScroll = () => {},
    onScrollFrame = () => {},
    scrollbarRef = null,
    hideFade = false,
    renderVertical = true,
    renderHorizontal = false,
    dynamicHeight = false,
    renderLeftSide = false,
}: {
    children: any;
    maxHeight: string;
    onScroll?: () => void;
    onScrollFrame?: (pos: positionValues) => void;
    scrollbarRef?: any;
    hideFade?: boolean;
    renderVertical?: boolean;
    renderHorizontal?: boolean;
    dynamicHeight?: boolean;
    renderLeftSide?: boolean;
}) => {
    const breakpoints = useBreakpoint();

    const [showTopOverlay, setShowTopOverlay] = useState(false);
    const [showBottomOverlay, setShowBottomOverlay] = useState(false);
    const [showLeftOverlay, setShowLeftOverlay] = useState(false);
    const [showRightOverlay, setShowRightOverlay] = useState(false);

    const handleUpdate = ({ top, left, clientHeight, scrollHeight, clientWidth, scrollWidth }: positionValues) => {
        setShowTopOverlay(top !== 0 && renderVertical);
        setShowBottomOverlay(1 - top > 0.001 && clientHeight !== scrollHeight && renderVertical);
        setShowLeftOverlay(left !== 0 && renderHorizontal);
        setShowRightOverlay(left !== 1 && clientWidth !== scrollWidth && renderHorizontal);
    };

    const styleHorizontal = { display: renderHorizontal && breakpoints.landscape ? "block" : "none" };
    const styleVertical = { display: renderVertical && breakpoints.landscape ? "block" : "none" };
    return (
        <Container>
            <Scrollbars
                ref={scrollbarRef}
                style={{
                    maxHeight,
                    maxWidth: "100%",
                    margin: "0px",
                }}
                onScrollStart={onScroll}
                onScrollFrame={onScrollFrame}
                onUpdate={handleUpdate}
                renderThumbHorizontal={props => (
                    <ThumbHorizontal {...props} className="thumb-horizontal" style={styleHorizontal}>
                        <HandleHorizontal />
                    </ThumbHorizontal>
                )}
                renderThumbVertical={props => (
                    <ThumbVertical {...props} className="thumb-vertical" style={styleVertical}>
                        <HandleVertical />
                    </ThumbVertical>
                )}
                renderTrackHorizontal={props => (
                    <TrackHorizontal {...props} className="track-horizontal" style={styleHorizontal} />
                )}
                renderTrackVertical={props => (
                    <TrackVertical
                        {...props}
                        leftSided={renderLeftSide}
                        className="track-vertical"
                        style={styleVertical}
                    />
                )}
                renderView={() => (
                    <ScrollView
                        isMobile={!breakpoints.landscape}
                        renderHorizontal={renderHorizontal}
                        renderVertical={renderVertical}
                        showTopOverlay={showTopOverlay}
                        showLeftOverlay={showLeftOverlay}
                        showRightOverlay={showRightOverlay}
                        showBottomOverlay={showBottomOverlay}
                        hideFade={hideFade}
                        className="view"
                        maxHeight={maxHeight}
                        dynamicHeight={dynamicHeight}
                        leftSided={renderLeftSide}
                    />
                )}
            >
                {children}
            </Scrollbars>
        </Container>
    );
};

export default CustomScrollbar;
